import { Link } from 'react-router-dom';
import { CheckIcon, StarIcon, ShieldCheckIcon, ClockIcon } from '@heroicons/react/24/solid';
import { Button } from '@/components/ui/button';
import { useEarlyAdopter } from '@/contexts/EarlyAdopterContext';
import { createEarlyAdopterHandler } from '@/utils/earlyAdopterInterceptor';
import { usePricingData } from '@/hooks/usePricingData';

const PricingPreview = () => {
  const { openModal } = useEarlyAdopter();
  const { getPlan } = usePricingData();

  const maxPrice = getPlan('thrivemax')?.monthlyPrice ?? 29;
  const famPrice = getPlan('thrivefamily')?.monthlyPrice ?? 59;

  const plans = [
    {
      name: "Independent Living",
      subtitle: "Essential",
      trial: "Free for 7 Days",
      price: `$${maxPrice}`,
      period: "/mo after trial",
      tagline: "Your daily structure for living safely at home.",
      highlights: [
        "Unlimited Home Safety Checks",
        "ThriveScore tracking",
        "Daily check-ins & reminders",
        "Medication tracking",
        "You + 1 family member included"
      ],
      popular: true,
      guarantee: true,
      noCreditCard: true
    },
    {
      name: "Family Support",
      subtitle: "Family",
      trial: "Free to explore",
      price: `$${famPrice}`,
      period: "/mo",
      tagline: "Independence at home. Strength together.",
      highlights: [
        "Everything in Essential",
        "Shared family view of wellness and safety",
        "Task coordination and care scheduling",
        "Medication log visible to the whole circle",
        "Real-time activity and check-in signals",
        "Older adult + up to 4 family members"
      ],
      billingNote: "Billing starts only when your loved one joins",
      popular: false,
      familyActivation: true
    }
  ];

  return (
    <section className="py-20 px-4 md:px-8 bg-white">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
            Simple, clear pricing
          </h2>
          <p className="text-lg text-muted-foreground">
            Start free. Upgrade when you're ready.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-stretch">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative rounded-md p-8 flex flex-col transition-all duration-300 ${
                plan.popular
                  ? 'shadow-overlay'
                  : 'bg-white shadow-raised hover:shadow-floating'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <StarIcon className="w-3 h-3" />
                    Most Popular
                  </span>
                </div>
              )}

              <div className="mb-4">
                <h3 className="font-bold text-xl text-foreground">{plan.name}</h3>
                <p className="text-sm text-muted-foreground">{plan.subtitle}</p>
              </div>

              <div className="flex items-center gap-2 mb-2">
                <ClockIcon className="w-6 h-6 text-primary" />
                <span className="text-sm font-bold text-primary">{plan.trial}</span>
              </div>

              <div className="flex items-baseline gap-1 mb-3">
                <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                <span className="text-muted-foreground">{plan.period}</span>
              </div>

              <p className="text-muted-foreground text-sm mb-6">{plan.tagline}</p>

              {/* Features list - grows to fill available space */}
              <ul className="space-y-3 mb-6 flex-grow">
                {plan.highlights.map((highlight, hIndex) => (
                  <li key={hIndex} className="flex items-start gap-2 text-sm text-foreground/80">
                    <CheckIcon className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>

              {plan.billingNote && (
                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-md mb-6">
                  <ShieldCheckIcon className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="text-xs text-muted-foreground">{plan.billingNote}</span>
                </div>
              )}

              {plan.familyActivation && (
                <div className="p-4 bg-accent/30 rounded-md mb-4">
                  <p className="text-xs text-foreground/80 leading-relaxed">
                    <span className="font-bold">Family-first activation:</span> Explore the Family plan free. Billing begins only after the older adult joins and activates the household.
                  </p>
                </div>
              )}

              {plan.guarantee && (
                <div className="p-4 bg-accent/30 rounded-md mb-4">
                  <p className="text-xs text-foreground/80 leading-relaxed">
                    <span className="font-bold">No Lost Progress:</span> If you decide not to subscribe after 7 days, you automatically keep basic access in limited mode. Your data is always preserved.
                  </p>
                </div>
              )}

              {plan.noCreditCard && (
                <p className="text-center text-xs text-muted-foreground mb-6">
                  No credit card required to start.
                </p>
              )}

              {/* Button pinned to bottom via mt-auto */}
              <Button
                size="lg"
                className={`w-full mt-auto min-h-[56px] font-bold ${
                  plan.popular
                    ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-button-blue hover:shadow-floating'
                    : 'border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground shadow-flat hover:shadow-button-blue'
                }`}
                variant={plan.popular ? 'default' : 'outline'}
                onClick={plan.popular ? createEarlyAdopterHandler(openModal, 'Pricing Preview - Essential') : undefined}
                asChild={!plan.popular}
              >
                {plan.popular ? (
                  <span>Get Early Access</span>
                ) : (
                  <Link to="/thrivefamily">Explore Family Plan</Link>
                )}
              </Button>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Button variant="link" className="text-primary font-bold text-lg" asChild>
            <Link to="/pricing">See full pricing details →</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PricingPreview;
